/**
 * Recurrence rule expansion using rrule.js.
 *
 * Provides utilities for expanding RFC 5545 RRULE strings into individual
 * occurrence dates. Used by both desktop and PWA clients to render recurring
 * events and tasks on the calendar.
 */

import { RRule } from 'rrule';

export interface RecurringOccurrence {
  /** ISO 8601 start timestamp of this occurrence. */
  occurrenceStart: string;
  /** ISO 8601 end timestamp (null if original has no end). */
  occurrenceEnd: string | null;
}

/**
 * Expand a recurrence rule into individual occurrences within a date range.
 *
 * @param rruleString - RFC 5545 RRULE string (e.g. "FREQ=WEEKLY;BYDAY=MO,WE,FR")
 * @param eventStart - ISO 8601 start datetime of the base event
 * @param eventEnd - ISO 8601 end datetime of the base event (null if all-day or no end)
 * @param rangeStart - ISO 8601 start of the visible date range
 * @param rangeEnd - ISO 8601 end of the visible date range
 * @returns Array of occurrence objects with start/end timestamps
 */
export function expandRecurrenceRule(
  rruleString: string,
  eventStart: string,
  eventEnd: string | null,
  rangeStart: string,
  rangeEnd: string,
): RecurringOccurrence[] {
  try {
    const startDate = new Date(eventStart);
    const duration = eventEnd
      ? new Date(eventEnd).getTime() - startDate.getTime()
      : null;

    // Parse the RRULE string. rrule.js expects the RRULE value without the "RRULE:" prefix.
    const rule = RRule.fromString(`DTSTART:${formatDateForRRule(startDate)}\nRRULE:${rruleString}`);

    const rangeStartDate = new Date(rangeStart);
    const rangeEndDate = new Date(rangeEnd);

    // Get all occurrences within the visible range
    const occurrences = rule.between(rangeStartDate, rangeEndDate, true);

    return occurrences.map((occurrence) => {
      const occurrenceStart = occurrence.toISOString();
      const occurrenceEnd = duration !== null
        ? new Date(occurrence.getTime() + duration).toISOString()
        : null;

      return { occurrenceStart, occurrenceEnd };
    });
  } catch {
    // If the RRULE string is invalid, return empty array rather than crashing
    return [];
  }
}

/**
 * Format a Date as a UTC datetime string for rrule.js DTSTART.
 * Format: YYYYMMDDTHHMMSSZ
 */
function formatDateForRRule(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}
